import React, { useCallback, useContext, useReducer } from 'react';
import axios from 'axios';

import AuthContext from '../auth/AuthContext';
import MainContext from './MainContext';
import MainReducer from './MainReducer';

import {
  lookForColumns,
  lookForThemes,
  lookForValues,
  searchTables,
  searchVarsByText,
} from './audienceBuilderSelectors';

import {
  CREATE_INDEX,
  CREATE_PIVOT,
  GET_COLUMN,
  GET_MAPVIEW,
  GET_REGIONS,
  GET_TABLE,
  GET_VALUE,
  HANDLE_ERROR,
  HANDLE_PERMISSION_ERROR,
  LOADING,
  LOADING_COMPLETE,
  LOADING_DECREMENT,
  LOADING_INCREMENT,
  PIVOT_COLUMNS,
  REFRESH,
  SET_AUDIENCE_SIZE,
  SET_IDS,
  SET_MAPCOND,
  SET_NON_PERSISTENT_DATASET,
  SET_PERSISTENT_DATASET,
  SET_PROGRESS,
  SET_SPECIFIC_PROGRESS,
  SET_CRM,
  RESET_IDS,
  SET_LOADING_LABEL,
  RESET_PERSONA,
  PIVOT_REFRESH,
  CLEAR_MAIN,
} from '../types';

import {
  AUDTYPE,
  BEHAVIOURTYPE,
  CRMTYPE,
  CROSSMEDIATYPE,
  CROSSTYPE,
  CSVTYPE,
  DEFINETYPE,
  DIALOGTYPE,
  LANDINGTYPE,
  LITYPE,
  MEDIATYPE,
  MMMTYPE,
  VARIABLEMAPPINGTYPE,
} from '../../components/Utilities/NamingConversions';
import { customLocationVariable } from '@constants/customLocationsConstants';

import { PersonaBuilderService } from '@api/pb';

const personaBuilderService = new PersonaBuilderService();

const defaultLoadingState = {
  [DIALOGTYPE]: 0,
  [LITYPE]: 0,
  [AUDTYPE]: 0,
  [MEDIATYPE]: 0,
  [CROSSTYPE]: 0,
  [BEHAVIOURTYPE]: 0,
  [CROSSMEDIATYPE]: 0,
  [LANDINGTYPE]: 0,
  [DEFINETYPE]: 0,
  [CSVTYPE]: 0,
  [CRMTYPE]: 0,
  [VARIABLEMAPPINGTYPE]: 0,
  [MMMTYPE]: 0,
};

function MapState(props) {
  const authContext = useContext(AuthContext);

  const initialState = {
    tables: [],
    columns: [],
    values: [],
    mapdata: {},
    mapdataCache: {},
    datasetsList: {},
    selectedDatasets: [],
    dbOptions: { tables: [], columns: [], values: [] },
    question: 1,
    loading: defaultLoadingState,
    progress: 'selectvar',
    mapcond: {},
    mapcode: [],
    mapdisplay: [],
    mapLayout: {},
    nickname: '',
    defineCus: {},
    csv: { loading: false, data: [] },
    ids: null,
    isPersistentIds: true, // used to determine if we should get ids from persistent table or not -- persistent if audience is saved or in report
    dataset: null, // eg; sp2023q1_ca
    geoLocation: null, // li module viewport location
    indexdata: {},
    pivotdata: { raw: [], percentage: [], largest: [], second: [] },
    pivotVars: [],
    pivotRegion: 'National',
    storedata: {},
    storeName: [],
    topStoreName: [],
    oohData: {},
    oohCompanyName: [],
    oohAnalytics: {},
    retailFlowMap: {},
    retailAnalytics: {},
    fsaAnalytics: {},
    driveTimeLayer: {},
    audienceTemplate: {},
    // kmlName: {},
    kmlData: {},
    pivotColumns: [],
    error: { text: '', status: false },
    permissionError: { text: '', status: false },
    pivotCond: null,
    mediaUsage: {},
    mediaChannels: {},
    demSummary: {},
    audiences: { cols: [], sqlWhere: '' },
    audiencePopulation: {},
    audienceSize: 0,
    qbFields: {},
    variableMapping: [],
    CRM: false,
    loadingLabels: [], // a queue used to display loading messages on the screen.
  };

  const [state, dispatch] = useReducer(MainReducer, initialState);

  // =====FUNCTION MAIN MODULES=====
  // ===============================
  //
  // =DEFINE=
  //	-getTables
  //	-getColumns
  //	-getValues
  //	-getMapview
  //	-extractMetadata
  //	-getIds
  //	-getDemoIds
  //	-createPivot
  //	-setSelectVars
  //
  // =AUDIENCE OVERVIEW=
  //	-getDemographicsProfiling
  //  -getProfilingSegments
  //  -getAudienceCount
  //
  // =MEDIA USAGE=
  //	-getMediaUsage
  //
  // =LI=
  //	-fetchMap
  //	-extractCSV
  //	-extractFSA
  //	-getStoreName
  //	-extractStores
  //	-getOOHCompanyName
  //  -getOOHAnalytics
  //  -getRetailFlowMap
  //  -getRetailAnalytics
  //  -getFSAAnalytics
  //  -getDriveTime
  //	-extractOOH
  //	-updateGeojsonSelected
  //	-setUndefinedIds
  //	-getDirectMailPrice (export related)
  //
  // =AUDIENCE CROSSTAB=
  // 	-getThemes
  //
  // =BEHAVIOUR DISCOVERY=
  //	-createIndex
  //
  // =MISC/UNUSED/UKNOWN=
  //	-refresh (all? should reset loading inside it?)
  //	-uploadFiles (smartlist / dropzone)
  //	-handleError (all? should reset loading inside it)
  //	-getIPGeoLocation (dashboard)
  //	-csvCheck (smartlist / dropzone)

  const loadingIncrement = (type, loadingDescription = 'Loading Data') => {
    dispatch({
      type: LOADING_INCREMENT,
      payload: {
        type: type,
      },
    });
    const loadingLabelsUpdated = [...state.loadingLabels];
    loadingLabelsUpdated.push(loadingDescription);
    dispatch({
      type: SET_LOADING_LABEL,
      payload: loadingLabelsUpdated,
    });
  };

  const loadingDecrement = (type) => {
    dispatch({
      type: LOADING_DECREMENT,
      payload: {
        type: type,
      },
    });
    const loadingLabelsUpdated = [...state.loadingLabels];
    loadingLabelsUpdated.shift();
    dispatch({
      type: SET_LOADING_LABEL,
      payload: loadingLabelsUpdated,
    });
  };

  // This function is used to retrieve list of themes with respective Categories (1st and 2nd column)
  const getThemes = useCallback(() => {
    try {
      const result = lookForThemes(state.variableMapping);
      return result;
    } catch (err) {
      handleError(err);
    }
  }, [authContext, state.variableMapping]); //eslint-disable-line

  // Search tables (Categories in FE) with respective code (Attributes in FE)
  const getTables = useCallback(
    async () => {
      try {
        const result = searchTables(state.variableMapping);
        dispatch({
          type: GET_TABLE,
          payload: result,
        });
      } catch (err) {
        handleError(err);
      }
    },

    [authContext, state.variableMapping] //eslint-disable-line
  );

  // search column (Attributes in FE) based on passed table (Category in FE)
  function getColumns(table) {
    try {
      const result = lookForColumns(state.variableMapping, table);
      dispatch({
        type: GET_COLUMN,
        payload: result,
      });
      return result;
    } catch (err) {
      handleError(err);
    }
  }

  // Only used by Simple Builder. Get Values (Variables in Simple Builder), based on column (Attribute)
  async function getValues({ filter, type = DEFINETYPE } = {}) {
    try {
      const result = lookForValues(state.variableMapping, filter);
      dispatch({
        type: GET_VALUE,
        payload: result,
      });
      return result;
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  // Function used in autocomplete components to get matching options.
  function searchVars(searchString, isAdanced) {
    try {
      const result = searchVarsByText(
        state.variableMapping,
        searchString,
        isAdanced
      );
      return result;
    } catch (err) {
      handleError(err);
    }
  }

  const getMapview = (data) => {
    dispatch({
      type: GET_MAPVIEW,
      payload: data,
    });
  };

  // A function that retrieves variable_mapping table
  async function extractMetadata({
    type = DEFINETYPE,
    subscriptionModule = 'any',
    filterDatasets = [],
  }) {
    loadingIncrement(type);
    try {
      const token = await authContext.getIdToken();
      let startTime = new Date().getTime();
      const res = await axios.post(
        '/get_metadata',
        {
          countryCode: authContext.userCountryCode,
          subscriptionModule: subscriptionModule,
          filterDatasets: filterDatasets,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );

      res.data.push(customLocationVariable);

      let endTime = new Date().getTime();
      let duration = endTime - startTime;
      // initial set state for the unique dataset names designated to their country's variable mapping
      // if this api is re-called and the country is already in the datasetsList object
      // it will ignore and skip this set state
      const uniqueNames = [
        ...new Set(
          res.data.map((val) => val.dataset).filter((val) => val !== 'None')
        ),
      ];
      if (!(authContext.userCountryCode in state.datasetsList)) {
        const resultObj = {
          country: authContext.userCountryCode,
          datasetArr: uniqueNames,
        };
        dispatch({ type: 'SET_DATASETS', payload: resultObj });
      }
      updateSelectedDatasets(uniqueNames);
      dispatch({ type: 'SET_VARIABLE_MAPPING', payload: res.data });
      // temporary post request to log performance for extract_metadata path on a client side.
      axios.post(
        '/metadata_performance',
        {
          duration: duration,
          countryCode: authContext.userCountryCode,
          subscriptionModule: subscriptionModule,
        },
        {
          headers: { Authorization: token },
        }
      );
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  /**
   *
   * @param {val} <arr> - array of datasets
   *
   * update the state of selectedDatasets in mainReducer
   */
  const updateSelectedDatasets = (val) => {
    dispatch({ type: 'UPDATE_SELECTED_DATASETS', payload: val });
  };

  const dumpIdsData = (source = false) => {
    // dumps all the data needed to recreate the current ids state
    // works in conjunction with loadIdsData
    // can dump from source or from state
    return {
      ids: source ? source.ids : state.ids,
      dataset: source ? source.dataset : state.dataset,
      cols: source ? source.cols : state.audiences.cols,
      sqlWhere: source ? source.sqlWhere : state.audiences.sqlWhere,
      cond: source
        ? source.cond
        : {
            cond: state.mapcond,
            code: state.mapcode,
            display: state.mapdisplay,
            population: state.audiencePopulation,
            qbFields: state.qbFields,
          },
      audience_size: source ? source.audience_size : state.audienceSize,
      pivotColumns: source ? source.cols : state.pivotColumns,
    };
  };

  const loadIdsData = (data) => {
    dispatch({
      type: SET_PERSISTENT_DATASET,
      payload: data.dataset,
    });
    dispatch({
      type: SET_PROGRESS,
    });
    dispatch({
      type: SET_MAPCOND,
      payload: data.cond,
    });
    dispatch({
      type: SET_IDS,
      payload: data,
    });
    dispatch({
      type: SET_AUDIENCE_SIZE,
      payload: data.audience_size,
    });
    dispatch({ type: PIVOT_COLUMNS, payload: data.cols });
    dispatch({ type: 'SET_GEO_LOCATION', payload: {} });
  };

  const getIds = async ({
    options,
    type = DEFINETYPE,
    subscriptionModule = 'any',
    dataset = false, // use specific dataset? false = use default
  }) => {
    // change default to either DIALOGTYPE or DEFINETYPE
    loadingIncrement(type);
    let data;
    try {
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/search/ids',
        {
          cond: options.advanced ? options.cond.code : options.cond.cond,
          advanced: options.advanced,
          sqlWhere: options.sqlWhere,
          allCountry: options.allCountry,
          subscriptionModule: subscriptionModule,
          countryCode: authContext.userCountryCode,
          locations: options?.locations ?? null,
          customLocationTable: options?.customLocationTable ?? null,
          mobilityToggle: options?.mobilityToggle ?? null,
          kwargs: {
            ...getPostKWArgs(),
            ...(dataset ? { dataset } : {}), // override dataset if specified
          },
        },
        {
          headers: { Authorization: token },
        }
      );
      data = res.data;
    } catch (err) {
      handleError(err);
      return 'error';
    } finally {
      loadingDecrement(type);
    }
    dispatch({
      type: SET_NON_PERSISTENT_DATASET,
      payload: data.dataset,
    });
    dispatch({
      type: SET_PROGRESS,
    });
    dispatch({
      type: SET_MAPCOND,
      payload: options.cond,
    });
    dispatch({
      type: SET_IDS,
      payload: data,
    });
    dispatch({
      type: SET_AUDIENCE_SIZE,
      payload: data.audience_size,
    });
    dispatch({ type: PIVOT_COLUMNS, payload: data.cols });
    dispatch({ type: 'SET_GEO_LOCATION', payload: {} });
    data['cond'] = options.cond;
    return dumpIdsData(data);
  };

  const getIdsDataset = () => {
    // used in post requests -- we need to know if the IDs are persistent or not
    // can also insert other kwargs to be passed directly to the back backend
    // in every post request.
    if (state.isPersistentIds) {
      return state.dataset;
    } else {
      return initialState.dataset;
    }
  };

  const getPostKWArgs = () => {
    // used in post requests -- we need to know if the IDs are persistent or not
    // can also insert other kwargs to be passed directly to the back backend
    // in every post request.
    const dataset = getIdsDataset();
    if (dataset) {
      return { dataset: dataset };
    } else {
      return {};
    }
  };

  const createPivot = async ({ pivot, pivotRegion, type = CROSSTYPE } = {}) => {
    try {
      loadingIncrement(type);

      // if no new pivot data provided, use last pivot data stored in state
      // always store last pivot data in state for future region switches
      const pivotToUse = pivot && pivot.length ? pivot : state.pivotVars;
      dispatch({
        type: 'SET_PIVOT_VARS',
        payload: { pivotVars: pivotToUse, pivotRegion },
      });

      let data = {};
      pivotToUse.map((p, i) => (data = { ...data, ['x' + (i + 1)]: p }));
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_audience_crosstab',
        {
          ids: state.ids,
          pivot: data,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
          ...(pivotRegion && pivotRegion !== 'National'
            ? { region: pivotRegion }
            : {}),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: CREATE_PIVOT,
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const setSelectVars = () => {
    dispatch({
      type: 'SET_SELECTVARS',
      payload: {
        ...initialState,
        variableMapping: state.variableMapping,
      },
    });
  };

  const setAudienceTemplate = (audience) => {
    dispatch({
      type: 'SET_AUDIENCE_TEMPLATE',
      payload: audience,
    });
  };

  const getDemographicsProfiling = async ({ type = AUDTYPE } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_audience_overview',
        {
          ids: state.ids,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'GET_DEMPROFILING',
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const getProfilingSegments = async ({ type = AUDTYPE, segment } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_profiling_segments',
        {
          ids: state.ids,
          segment,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'SET_PROFILING_SEGMENT',
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const getAudienceCount = async ({ type = AUDTYPE } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_audience_count',
        {
          ids: state.ids,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'SET_AUDIENCE_COUNT',
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const getMediaUsage = async ({ type = MEDIATYPE } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();

      const [mediaChannelsResp, mediaUsageResp] = await Promise.all([
        personaBuilderService.getMediaChannels(
          token,
          authContext.userCountryCode,
          getPostKWArgs()
        ),
        personaBuilderService.getMediaUsage(
          token,
          authContext.userCountryCode,
          state.ids,
          getPostKWArgs()
        ),
      ]);

      dispatch({
        type: 'SET_MEDIA_CHANNELS',
        payload: mediaChannelsResp.data,
      });
      dispatch({
        type: 'SET_MEDIA_USAGE',
        payload: mediaUsageResp.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const updateGeojsonSelected = (brand, selected, isOOH) => {
    dispatch({
      type: isOOH ? 'UPDATE_OOH_SELECTED' : 'UPDATE_STORE_SELECTED',
      payload: {
        brand: brand,
        selected: selected,
      },
    });
  };

  const setUndefinedIds = () => {
    dispatch({
      type: SET_IDS,
      payload: undefined,
    });
    dispatch({
      type: SET_NON_PERSISTENT_DATASET,
      payload: initialState.dataset,
    });
  };

  const getDirectMailPrice = async (features) => {
    try {
      const token = await authContext.getIdToken();
      await axios.post(
        '/get_direct_mail_price',
        {
          ids: state.ids,
          features: features,
          countryCode: authContext.userCountryCode,
        },
        {
          headers: { Authorization: token },
        }
      );
    } catch (err) {
      handleError(err);
    }
  };

  const getRegions = async ({ type = BEHAVIOURTYPE } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_behaviour_regions',
        {
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: GET_REGIONS,
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const createIndex = async ({
    table,
    province,
    type = BEHAVIOURTYPE,
  } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_audience_index',
        {
          ids: state.ids,
          table: table,
          province: province === 'National' ? false : province,
          countryCode: authContext.userCountryCode,
          kwargs: getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: CREATE_INDEX,
        payload: res.data,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const resetPersona = () => {
    dispatch({
      type: PIVOT_REFRESH,
    });
    dispatch({
      type: RESET_PERSONA,
    });
  };

  // auth
  const clearMainState = () => {
    dispatch({
      type: CLEAR_MAIN,
      payload: {
        ...initialState,
      },
    });
  };

  const refresh = () => {
    loadingIncrement(DEFINETYPE);
    dispatch({
      type: REFRESH,
      payload: {
        ...initialState,
        variableMapping: state.variableMapping,
        datasetsList: state.datasetsList,
        selectedDatasets: state.selectedDatasets,
        type: state.type,
      },
    });
    loadingDecrement(DEFINETYPE);
  };

  const handleError = (error) => {
    const response = error?.response;
    // Helper function to dispatch actions
    const dispatchError = (type, text) => {
      dispatch({
        type,
        payload: {
          text,
          status: true,
        },
      });
    };
    // Handling permission error
    if (response?.status === 403) {
      const errorMessage =
        response.data && response.data.status
          ? response.data.status
          : 'You do not have the required permissions to perform this action.';
      dispatchError(HANDLE_PERMISSION_ERROR, errorMessage);
    }
    // Handling other errors
    else if (response?.data?.status) {
      dispatchError(HANDLE_ERROR, response.data.status);
    } else {
      // this is not a server error, but a client error
      // let the user know that there was an error
      dispatchError(
        HANDLE_ERROR,
        'INTERNAL SERVER ERROR, PLEASE COME BACK LATER'
      );
      // then log the error to devs
      axios.post('/log_error', {
        userInfo: authContext?.userInfo || {},
        error: error.toString ? error.toString() : `${error}`,
        errorStack: error.stack ? error.stack.toString() : '',
      });
    }
  };

  const csvCheck = async (formData, type) => {
    try {
      dispatch({ type: LOADING });
      const token = await authContext.getIdToken();
      const res = await axios.post('/csv_check', formData, {
        headers: { Authorization: token },
      });
      dispatch({
        type: LOADING_COMPLETE,
      });
      return res.data;
    } catch (err) {
      handleError(err);
    }
  };

  const dispatchReducer = useCallback((obj) => {
    dispatch(obj);
  }, []);

  const setAudience = ({ audience }) => {
    dispatch({
      type: 'SET_AUDIENCE',
      payload: audience,
    });
  };

  // This function is used to set specific progress stage
  const setProgress = (progress) => {
    dispatch({
      type: SET_SPECIFIC_PROGRESS,
      payload: progress,
    });
  };

  const setCRM = (status) => {
    dispatch({
      type: SET_CRM,
      payload: status,
    });
  };

  const resetAudienceIDs = () => {
    dispatch({
      type: RESET_IDS,
      payload: '',
    });
  };

  const sendEmail = async (templateId, variables = {}) => {
    loadingIncrement(DEFINETYPE);
    try {
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        '/send_email',
        { templateId, variables },
        {
          headers: { Authorization: token },
        }
      );
      return data.status; // usually 'OK'
    } catch (err) {
      if ([400, 404].includes(err.response?.status)) {
        // let the component calling this method handle known errors
        throw err;
      }
      // handle unknown errors and 403 permission denied
      handleError(err);
    } finally {
      loadingDecrement(DEFINETYPE);
    }
  };

  return (
    <MainContext.Provider
      value={{
        storeColors: state.storeColors,
        tables: state.tables,
        columns: state.columns,
        values: state.values,
        dbOptions: state.dbOptions,
        loading: state.loading,
        question: state.question,
        progress: state.progress,
        nickname: state.nickname,
        defineCus: state.defineCus,
        mapdata: state.mapdata,
        mapdataCache: state.mapdataCache,
        csv: state.csv,
        ids: state.ids,
        isPersistentIds: state.isPersistentIds,
        dataset: state.dataset,
        datasetsList: state.datasetsList,
        selectedDatasets: state.selectedDatasets,
        geoLocation: state.geoLocation,
        regions: state.regions,
        indexdata: state.indexdata,
        pivotdata: state.pivotdata,
        pivotVars: state.pivotVars,
        pivotRegion: state.pivotRegion,
        storedata: state.storedata,
        audienceTemplate: state.audienceTemplate,
        pivotColumns: state.pivotColumns,
        error: state.error,
        permissionError: state.permissionError,
        storeName: state.storeName,
        topStoreName: state.topStoreName,
        mapcond: state.mapcond,
        mapcode: state.mapcode,
        mapdisplay: state.mapdisplay,
        pivotCond: state.pivotCond,
        mediaUsage: state.mediaUsage,
        mediaChannels: state.mediaChannels,
        demSummary: state.demSummary,
        mapLayout: state.mapLayout,
        audiences: state.audiences,
        oohCompanyName: state.oohCompanyName,
        oohAnalytics: state.oohAnalytics,
        retailFlowMap: state.retailFlowMap,
        retailAnalytics: state.retailAnalytics,
        fsaAnalytics: state.fsaAnalytics,
        driveTimeLayer: state.driveTimeLayer,
        oohData: state.oohData,
        mobilityDates: state.mobilityDates,
        // kmlName: state.kmlName,
        kmlData: state.kmlData,
        audiencePopulation: state.audiencePopulation,
        audienceSize: state.audienceSize,
        qbFields: state.qbFields,
        variableMapping: state.variableMapping,
        CRM: state.CRM,
        loadingLabels: state.loadingLabels,
        searchVars,
        loadingIncrement,
        loadingDecrement,
        refresh,
        clearMainState,
        getThemes,
        getTables,
        getColumns,
        getValues,
        dispatch,
        getMapview,
        extractMetadata,
        dumpIdsData,
        loadIdsData,
        getIds,
        getIdsDataset,
        getPostKWArgs,
        getRegions,
        createIndex,
        resetPersona,
        createPivot,
        // uploadFiles,
        handleError,
        setSelectVars,
        setAudienceTemplate,
        csvCheck,
        setUndefinedIds,
        getMediaUsage,
        getDemographicsProfiling,
        dispatchReducer,
        updateGeojsonSelected,
        updateSelectedDatasets,
        getDirectMailPrice,
        getProfilingSegments,
        getAudienceCount,
        setAudience,
        setProgress,
        setCRM,
        resetAudienceIDs,
        sendEmail,
      }}
    >
      {props.children}
    </MainContext.Provider>
  );
}

export default MapState;
